import {Box, Stack, Typography, capitalize, useTheme} from '@mui/material';
import React, {Fragment, Suspense} from 'react';

import {AppointmentBookingEnum, BookingProcessSuccessEnum} from 'main/analytics/gaTagsEnum';
import {useBookingSummary} from 'presentation/modules/booking/shared/hooks/useBookingSummary';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {formatDateWithWeekday, formatText} from 'presentation/utils';
import {
	ConfirmBookingButton,
	ProviderAddressField,
	ReviewCard,
	SummarySectionGroup,
} from '../../components';
import {ChangeFlowVariantEnum} from '../../components/ChangeFlowDialog/constants';
import {ProviderAddressSkeleton} from '../../components/ProviderAddressField/ProviderAdressSkeleton';
import {SquareLabelTime} from '../../components/SquareLabelTime';
import {FlowEnum} from '../../constants';
import getFormattedHour from '../../utils/getFormattedHour';
import {ReviewBookingProps} from './types';
import {useBookingTheme} from 'presentation/modules/booking/shared/hooks/useBookingTheme';

const {appointmentCity} = translations['pt-br'].privateFlow.profile;
const {professionalAndDate, at, specialty, exam} = translations['pt-br'].commons;
const {appointmentSummary} = translations['pt-br'].privateFlow.bookingFlow;

export function ReviewBooking({
	flowType,
	handlePrevStep,
	activeStep,
	stepLabels,
}: ReviewBookingProps) {
	const bookingData = useBookingStore(state => state.bookingData);
	const {colors} = useTheme();
	const {isLaptop, isMobile, isTablet} = useMediaQueries();
	const {handleChangeModals} = useBookingSummary();
	const {iconName} = useBookingTheme({flowType});

	const isExam = flowType === FlowEnum.EXAM;
	const isTelehealth = flowType === FlowEnum.TELEHEALTH;
	const {isRebooking, isFavoriteDoctorsFlow} = bookingData;

	const {day, weekday, month} = formatDateWithWeekday(bookingData.dateAt);

	const formattedHour = getFormattedHour(bookingData.hourAt, flowType);
	const formattedDateAndHour = `${capitalize(weekday)}, ${bookingData.dateAt} ${at} ${formattedHour}`;

	const getLocationPrimaryText = () => formatText(bookingData.cityName);
	const getLocationSecondaryText = () => formatText(bookingData.ufName);
	return (
		<Fragment>
			<Stack
				gap={2}
				m={0}
				data-tag-id={BookingProcessSuccessEnum.ACESSOU_ETAPA_REVISAO_AGENDAMENTO}>
				<Typography variant="subtitle2" color={colors.light.text.secondary}>
					{appointmentSummary}
				</Typography>
				<ReviewCard.Root>
					<ReviewCard.Header
						handleAction={handlePrevStep}
						subheader={professionalAndDate}
						actionDataTagId={
							AppointmentBookingEnum.CLICK_ALTERAR_INFORMACOES_AGENDAMENTO
						}
					/>
					<Stack flexDirection={'row'} gap={2} alignItems={'center'}>
						<SquareLabelTime day={day} month={month} />
						<Stack>
							<Typography variant="subtitle2" fontSize={'1.125rem'}>
								{formattedDateAndHour}
							</Typography>
							<ReviewCard.Content
								iconName="medical-services-filled"
								primaryText={bookingData.professionalName}
							/>
						</Stack>
					</Stack>
				</ReviewCard.Root>

				<ReviewCard.Root>
					<ReviewCard.Header
						handleAction={
							isRebooking || isFavoriteDoctorsFlow
								? undefined
								: () => handleChangeModals(ChangeFlowVariantEnum.SPECIALTY)
						}
						subheader={isExam ? exam : specialty}
						actionDataTagId={
							AppointmentBookingEnum.CLICK_ALTERAR_INFORMACOES_AGENDAMENTO
						}
					/>
					<ReviewCard.Content
						iconName={iconName}
						primaryText={bookingData.specialtyName}></ReviewCard.Content>
				</ReviewCard.Root>

				<Box
					display={'grid'}
					gridTemplateColumns={isTablet || isTelehealth ? '1fr' : '1fr 1fr'}
					gap={2}>
					<Suspense fallback={<ProviderAddressSkeleton />}>
						<ProviderAddressField flowType={flowType} />
					</Suspense>
					{isTelehealth ? null : (
						<ReviewCard.Root>
							<ReviewCard.Header
								handleAction={
									isRebooking || isFavoriteDoctorsFlow
										? undefined
										: () => handleChangeModals(ChangeFlowVariantEnum.LOCATION)
								}
								subheader={appointmentCity}
								actionDataTagId={
									AppointmentBookingEnum.CLICK_ALTERAR_INFORMACOES_AGENDAMENTO
								}
							/>
							<ReviewCard.Content
								iconName={'my-location-filled'}
								primaryText={getLocationPrimaryText()}
								secondaryText={getLocationSecondaryText()}></ReviewCard.Content>
						</ReviewCard.Root>
					)}
				</Box>

				<Stack gap={2}>
					{isLaptop ? (
						<SummarySectionGroup
							noBorderRadius={isMobile}
							showTitle={false}
							flowType={flowType}
							activeStep={activeStep}
							handleBack={handlePrevStep}
							stepLabels={stepLabels}
							handleChangeModals={handleChangeModals}
						/>
					) : null}

					{isMobile ? (
						<ConfirmBookingButton
							flowType={flowType}
							variant="fixed"
							handleBack={handlePrevStep}
						/>
					) : (
						<ConfirmBookingButton
							gap={1}
							flexDirection="row"
							justifyContent={'end'}
							flowType={flowType}
							handleBack={handlePrevStep}
							fullWidth={false}
						/>
					)}
				</Stack>
			</Stack>
		</Fragment>
	);
}
